import { path } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { breakpoints } from '@/styles'
import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import Icon from '@/components/icon'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import StripeLogin from '@/components/stripe-login'
import useProfile from '@/hooks/use-profile'
import useBetaUser from '@/hooks/use-beta-user'
import useCurrentSession from '@/hooks/use-current-session'
import { useFlag } from '@/flags'
import { Stack } from '@/components/structure'
import {
  DashboardRow,
  DashboardContent,
  DashboardTitle,
  DashboardDescription
} from '@/components/settings'

const StyledPage = styled(Page)`
  height: 100%;
`

const StyledDashboardRow = styled(DashboardRow)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;
  }
`
const StyledDashboardContent = styled(DashboardContent)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 100%;
    margin-bottom: ${props => props.bottom ?? 24}px;
    text-align: center;
    align-items: center;
  }
`
const StyledDashboardTitle = styled(DashboardTitle)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: unset;
  }
`
const StyledDashboardDescription = styled(DashboardDescription)`
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin-right: unset;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  max-width: 748px;
  width: 100%;
  margin: auto;
  padding: 0 16px;
  flex: 1;
`

const StyledPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: 8px;
`

const PaymentSettingsDashboard = () => {
  const { t } = useTranslation()
  const profile = useProfile()
  const stripeConnected = profile.company_info?.ach_connected.includes('stripe')
  const directDepositConnected = profile.company_info?.ach_connected.includes('upop')
  const userRole = useSelector(path(['session', 'session', 'user_role']))
  const isAdmin = userRole === 'administrator'
  const session = useCurrentSession()
  const betaUser = useBetaUser()
  const betaUserPad = betaUser.pad
  const currentBetaUser = betaUserPad.includes(parseInt(session.id))
  const showPad = useFlag(['features', 'pad'], false) && currentBetaUser

  const adminTitles = {
    none: t(
      'l.payment_settings.view.setup_payment_options.text',
      'Set up these options to collect rental payments'
    ),
    some: t(
      'l.payment_settings.view.setup_payment_options.text',
      'Set up these options to collect rental payments'
    ),
    all: t('l.payment_settings.view.you_are_all_set.text', `You're all set!`)
  }

  const agentTitles = {
    none: t(
      'l.payment_settings.view.contact_admin_to_add_payment_methods.text',
      `Contact your company's administrator to add payment methods`
    ),
    some: t(
      'l.payment_settings.view.contact_admin_to_add_view_payment_methods.text',
      `Contact your company's administrator to add an additional payment method or see payment method details`
    ),
    all: t(
      'l.payment_settings.view.contact_admin_to_view_payment_methods.text',
      `Contact your company's administrator to view payment method details`
    )
  }

  const getSectionTitle = () => {
    const accounts = [stripeConnected, directDepositConnected]
    const total = accounts.length
    const connected = accounts.filter(Boolean).length
    const titles = isAdmin ? adminTitles : agentTitles
    if (connected === 0) return titles.none
    return connected === total ? titles.all : titles.some
  }

  return (
    <StyledPage>
      <ContentWrapper>
        <PageTitle>{t('l.settings.view.payment_settings.title', 'Payment Settings')}</PageTitle>
        <AreaTitle>{getSectionTitle()}</AreaTitle>
        <Stack gutter={24}>
          <StyledPanel hSpace={0} vSpace={0}>
            <StyledDashboardRow>
              <StyledDashboardContent>
                <StyledDashboardTitle>
                  {directDepositConnected && (
                    <StyledIcon
                      id="checked_green"
                      width={24}
                      height={24}
                      data-testid="check-direct-deposit"
                    />
                  )}
                  {t('l.payment_settings.view.direct_deposit.label', 'Direct Deposit')}
                </StyledDashboardTitle>
                <StyledDashboardDescription>
                  {showPad
                    ? t(
                        'l.payment_settings.view.direct_deposit_add_description.text',
                        'This allows your tenants to pay by Bitcoin and Pre-Authorized Debits (PAD).'
                      )
                    : 'This allows your tenants to pay by Bitcoin.'}
                </StyledDashboardDescription>
              </StyledDashboardContent>
              {directDepositConnected ? (
                <Button
                  theme="secondary"
                  to="/settings/payment-settings/direct-deposit"
                  disabled={!isAdmin}
                  data-testid="view-direct-deposit"
                >
                  {t('l.payment_settings.view.manage_accounts.button', 'Manage Accounts')}
                </Button>
              ) : (
                <Button
                  theme="primary"
                  to="/settings/payment-settings/direct-deposit"
                  disabled={!isAdmin}
                  data-testid="connect-direct-deposit"
                >
                  {t('l.payment_settings.view.connect.button', 'Connect')}
                </Button>
              )}
            </StyledDashboardRow>
          </StyledPanel>
          <StyledPanel hSpace={0} vSpace={0}>
            <StyledDashboardRow>
              <StyledDashboardContent>
                <StyledDashboardTitle>
                  {stripeConnected && (
                    <StyledIcon
                      id="checked_green"
                      width={24}
                      height={24}
                      data-testid="check-stripe"
                    />
                  )}
                  {t('l.payment_settings.stripe_account.label', 'Stripe Account')}
                </StyledDashboardTitle>
                <StyledDashboardDescription>
                  {t(
                    'l.payment_settings.view.stripe_add_description.text',
                    'This allows your tenants to pay by Visa / Mastercard / American Express / UnionPay.'
                  )}
                </StyledDashboardDescription>
              </StyledDashboardContent>
              <StripeLogin
                path="/settings/payment-settings"
                isConnected={stripeConnected}
                disabled={!isAdmin}
              />
            </StyledDashboardRow>
          </StyledPanel>
        </Stack>
      </ContentWrapper>
    </StyledPage>
  )
}

export default PaymentSettingsDashboard
